import { useCallback } from 'react'
import { useAuth } from '@wehunt/core-web'
import type { ChangePasswordOutcome } from '@wehunt/ui'
import { changePasswordFailure } from './changePasswordFailure'

/**
 * The Settings page's change-password handler.
 *
 * Firebase Auth owns passwords — there is no clubhouse API endpoint for one —
 * so this reaches it through the shared auth surface and translates its error
 * codes into the reasons the page can render. It resolves rather than throws so
 * the modal always has a definite outcome to show; the page claims success only
 * when this returns `ok`.
 */
export function useChangePassword(): (
  currentPassword: string,
  newPassword: string,
) => Promise<ChangePasswordOutcome> {
  const { changePassword } = useAuth()
  return useCallback(
    async (currentPassword, newPassword) => {
      try {
        await changePassword(currentPassword, newPassword)
        return { ok: true }
      } catch (error: unknown) {
        return { ok: false, reason: changePasswordFailure(error) }
      }
    },
    [changePassword],
  )
}
